import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useState, useEffect } from 'react';
import { X, Bell, Volume2, VolumeX } from 'lucide-react';
import { boardSupabaseService } from '../../services/board-supabase.service';
const notificationTypes = [
    {
        id: 'mentioned_enabled',
        label: 'Mentioned me',
        description: 'Get notified when someone mentions you',
        icon: Bell
    },
    {
        id: 'assigned_enabled',
        label: 'Assigned me',
        description: 'Get notified when tasks are assigned to you',
        icon: Bell
    },
    {
        id: 'automations_enabled',
        label: 'Automations with notify step',
        description: 'Get notified when automations run with notify actions',
        icon: Bell
    }
];
export const BoardNotificationsModal = ({ isOpen, onClose, boardId, userId, isDark, }) => {
    const [preferences, setPreferences] = useState({
        is_global_muted: false,
        mentioned_enabled: true,
        assigned_enabled: true,
        automations_enabled: true
    });
    const [isLoading, setIsLoading] = useState(false);
    useEffect(() => {
        if (isOpen) {
            loadPreferences();
        }
    }, [isOpen]);
    const loadPreferences = async () => {
        setIsLoading(true);
        try {
            const prefs = await boardSupabaseService.getBoardNotificationPreferences(boardId, userId);
            if (prefs) {
                setPreferences(prefs);
            }
        }
        catch (error) {
            console.error('Failed to load notification preferences:', error);
        }
        finally {
            setIsLoading(false);
        }
    };
    const handleGlobalMuteChange = async (muted) => {
        const newPrefs = { ...preferences, is_global_muted: muted };
        setPreferences(newPrefs);
        try {
            await boardSupabaseService.setBoardNotificationPreferences(boardId, userId, {
                is_global_muted: muted
            });
        }
        catch (error) {
            console.error('Failed to update global mute:', error);
            setPreferences(preferences);
        }
    };
    const handleNotificationTypeChange = async (type, enabled) => {
        const newPrefs = { ...preferences, [type]: enabled };
        setPreferences(newPrefs);
        try {
            await boardSupabaseService.updateNotificationPreference(boardId, userId, type, enabled);
        }
        catch (error) {
            console.error('Failed to update notification preference:', error);
            setPreferences(preferences);
        }
    };
    if (!isOpen)
        return null;
    return (_jsxs(_Fragment, { children: [_jsx("div", { className: "fixed inset-0 bg-black bg-opacity-50 z-40", onClick: onClose }), _jsxs("div", { className: "fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-full max-w-xl rounded-lg shadow-xl z-50 border", style: {
                    backgroundColor: isDark ? '#1a1a1a' : '#ffffff',
                    borderColor: isDark ? '#333333' : '#e5e7eb'
                }, children: [_jsxs("div", { className: "flex items-center justify-between p-6 border-b", style: {
                            borderColor: isDark ? '#333333' : '#e5e7eb',
                            backgroundColor: isDark ? '#2a2a2a' : '#f9fafb'
                        }, children: [_jsx("h2", { className: "text-xl font-bold", style: { color: isDark ? '#ffffff' : '#000000' }, children: "Notifications" }), _jsx("button", { onClick: onClose, className: "p-2 rounded-lg hover:bg-opacity-50 transition-colors", style: {
                                    backgroundColor: isDark ? '#333333' : '#e5e7eb'
                                }, children: _jsx(X, { className: "w-5 h-5", style: { color: isDark ? '#ffffff' : '#000000' } }) })] }), _jsxs("div", { className: "p-6 space-y-6", children: [_jsx("div", { className: "p-4 rounded-lg border", style: {
                                    backgroundColor: isDark ? '#2a2a2a' : '#f9fafb',
                                    borderColor: isDark ? '#444444' : '#e5e7eb'
                                }, children: _jsxs("div", { className: "flex items-center justify-between", children: [_jsxs("div", { children: [_jsx("p", { className: "font-semibold", style: { color: isDark ? '#ffffff' : '#000000' }, children: "Mute all notifications" }), _jsx("p", { className: "text-sm", style: { color: isDark ? '#888888' : '#6b7280' }, children: "Turn off all board notifications" })] }), _jsx("button", { onClick: () => handleGlobalMuteChange(!preferences.is_global_muted), disabled: isLoading, className: "p-2 rounded-lg transition-colors", style: {
                                                backgroundColor: preferences.is_global_muted ? '#EF4444' : isDark ? '#444444' : '#e5e7eb'
                                            }, children: preferences.is_global_muted ? (_jsx(VolumeX, { className: "w-5 h-5", style: { color: '#ffffff' } })) : (_jsx(Volume2, { className: "w-5 h-5", style: { color: isDark ? '#888888' : '#6b7280' } })) })] }) }), _jsxs("div", { children: [_jsx("p", { className: "text-sm font-semibold mb-4", style: { color: isDark ? '#aaaaaa' : '#6b7280' }, children: "Notification types" }), _jsx("div", { className: "space-y-3", children: notificationTypes.map(type => (_jsxs("div", { className: "p-4 rounded-lg border flex items-center justify-between", style: {
                                                backgroundColor: isDark ? '#2a2a2a' : '#f9fafb',
                                                borderColor: isDark ? '#444444' : '#e5e7eb',
                                                opacity: preferences.is_global_muted ? 0.6 : 1,
                                                pointerEvents: preferences.is_global_muted ? 'none' : 'auto'
                                            }, children: [_jsxs("div", { className: "flex-1", children: [_jsx("p", { className: "font-medium", style: { color: isDark ? '#ffffff' : '#000000' }, children: type.label }), _jsx("p", { className: "text-sm", style: { color: isDark ? '#888888' : '#6b7280' }, children: type.description })] }), _jsx("button", { onClick: () => {
                                                        const newValue = !preferences[type.id];
                                                        handleNotificationTypeChange(type.id, newValue);
                                                    }, disabled: isLoading || preferences.is_global_muted, className: "p-2 rounded-lg transition-colors ml-3", style: {
                                                        backgroundColor: preferences[type.id] && !preferences.is_global_muted
                                                            ? '#3B82F6'
                                                            : isDark
                                                                ? '#444444'
                                                                : '#e5e7eb'
                                                    }, children: _jsx(Bell, { className: "w-5 h-5", style: {
                                                            color: preferences[type.id] && !preferences.is_global_muted
                                                                ? '#ffffff'
                                                                : isDark
                                                                    ? '#888888'
                                                                    : '#6b7280'
                                                        } }) })] }, type.id))) })] }), _jsx("p", { className: "text-xs text-center p-3 rounded-lg", style: {
                                    backgroundColor: isDark ? '#2a2a2a' : '#f0f9ff',
                                    color: isDark ? '#888888' : '#0369a1'
                                }, children: preferences.is_global_muted
                                    ? 'All board notifications are muted. Enable individual notification types to receive specific alerts.'
                                    : 'Customize how you receive notifications from this board.' })] })] })] }));
};
